/**
 * Resizable divider for workspace columns
 * Supports drag to resize and stores position in localStorage
 */

import { useCallback, useRef, useEffect } from 'react';
import './ResizableDivider.css';

const STORAGE_KEY = 'workspace-column-width';
const MIN_WIDTH = 200;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 280;

interface ResizableDividerProps {
  onResize: (width: number) => void;
  initialWidth?: number;
}

export function getStoredWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const width = parseInt(stored, 10);
      if (width >= MIN_WIDTH && width <= MAX_WIDTH) {
        return width;
      }
    }
  } catch {
    // localStorage not available
  }
  return DEFAULT_WIDTH;
}

function storeWidth(width: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(width));
  } catch {
    // localStorage not available
  }
}

export function ResizableDivider({ onResize, initialWidth }: ResizableDividerProps) {
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(initialWidth ?? DEFAULT_WIDTH);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    startX.current = e.clientX;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;

      const delta = e.clientX - startX.current;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth.current + delta));
      onResize(newWidth);
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!isDragging.current) return;

      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      const delta = e.clientX - startX.current;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth.current + delta));
      startWidth.current = newWidth;
      storeWidth(newWidth);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [onResize]);

  // Update startWidth when initialWidth changes, but only if not currently dragging
  // During drag, startWidth must remain the value at drag start for correct delta calculation
  useEffect(() => {
    if (initialWidth !== undefined && !isDragging.current) {
      startWidth.current = initialWidth;
    }
  }, [initialWidth]);

  return (
    <div
      className="resizable-divider"
      onMouseDown={handleMouseDown}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize columns"
    >
      <div className="resizable-divider__handle" />
    </div>
  );
}
